import { createContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { CookieConsent, CookieConsentContextType } from '../types/cookieConsent';
import { readConsent, writeConsent, clearConsent, buildConsent } from '../utils/consentStorage';
import { initGa4ConsentDefaults, loadGa4, revokeGa4Consent } from '../utils/analytics';

// eslint-disable-next-line react-refresh/only-export-components
export const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent | null>(() => readConsent());

  useEffect(() => {
    initGa4ConsentDefaults();
  }, []);

  useEffect(() => {
    if (consent?.analytics) {
      loadGa4();
    } else {
      revokeGa4Consent();
    }
  }, [consent?.analytics]);

  const acceptAll = useCallback(() => {
    const newConsent = buildConsent(true);
    writeConsent(newConsent);
    setConsent(newConsent);
  }, []);

  const rejectAll = useCallback(() => {
    const newConsent = buildConsent(false);
    writeConsent(newConsent);
    setConsent(newConsent);
  }, []);

  const updatePreferences = useCallback((preferences: { analytics: boolean }) => {
    const newConsent = buildConsent(preferences.analytics);
    writeConsent(newConsent);
    setConsent(newConsent);
  }, []);

  const resetConsent = useCallback(() => {
    clearConsent();
    setConsent(null);
  }, []);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        hasConsented: consent !== null,
        acceptAll,
        rejectAll,
        updatePreferences,
        resetConsent,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}
